/**
 * CoConnect — Driver GPS Offline Adapter
 *
 * This module is intentionally thin. Durable state, outbox identity, audit,
 * retry classification and single-leader synchronization are owned by the
 * shared Offline Kernel.
 */
import { apiClient } from './api';
import { currentOfflineAuthorizationEnvelope, offlineActionDecision } from '../offline/authEnvelope';
import { getDeviceId } from '../offline/device';
import { getAllByIndex, idbRequest, withOfflineTransaction } from '../offline/db';
import { sha256Canonical } from '../offline/crypto';
import { getConnectivitySnapshot, backendUsable, subscribeConnectivity } from '../offline/connectivity';
import { registerSyncHandler, requestSync } from '../offline/syncCoordinator';
import type { LocalAuditEvent, OutboxCommand } from '../offline/types';
import { nextAuditSequence } from '../offline/audit';

export interface GpsPing {
  lat: number;
  lng: number;
  speedKmh?: number;
  heading?: number;
  odometerKm?: number;
  dispatchId?: string;
  timestamp: string;
  accuracyM?: number;
}

interface StoredGpsEvent extends GpsPing {
  event_id: string;
  tenant_id: string;
  user_id: string;
  device_id: string;
  sequence_no: number;
  sync_status: 'PENDING_SYNC' | 'SYNCING' | 'SYNCED' | 'FAILED';
  command_id: string;
  created_at: string;
}

const APP_VERSION = () => (import.meta as any).env?.VITE_APP_VERSION || '1.5.0-web';

async function nextSequence(tx: IDBTransaction, userId: string): Promise<number> {
  const meta = tx.objectStore('meta');
  const key = `driver_gps_sequence:${userId}`;
  const current: any = await idbRequest(meta.get(key));
  const next = Number(current?.value || 0) + 1;
  await idbRequest(meta.put({ key, value: next, updated_at: new Date().toISOString() }));
  return next;
}

export function isOnline(): boolean {
  return backendUsable(getConnectivitySnapshot().state);
}

export async function queueGpsPing(ping: GpsPing, userId: string): Promise<string> {
  if (!Number.isFinite(ping.lat) || !Number.isFinite(ping.lng) || ping.lat < -90 || ping.lat > 90 || ping.lng < -180 || ping.lng > 180) {
    throw new Error('Invalid GPS coordinate cannot be committed locally');
  }
  const decision = await offlineActionDecision(userId, 'CAPTURE_TRUCK_GPS');
  if (!decision.allowed) throw new Error(decision.reason);
  const envelope = decision.envelope!;
  const deviceId = getDeviceId();
  const commandId = crypto.randomUUID();
  const eventId = crypto.randomUUID();
  const payload = { ...ping, eventId, capturedAt: ping.timestamp };
  const payloadHash = await sha256Canonical(payload);
  const now = new Date().toISOString();

  await withOfflineTransaction(['meta', 'driver_gps_events', 'outbox_commands', 'audit_events'], 'readwrite', async tx => {
    // GPS sequence belongs to the Driver GPS event/outbox stream.
    const sequence = await nextSequence(tx, userId);

    // Audit sequence belongs to the global tenant+user audit stream.
    // Never reuse a domain-specific sequence for audit_events because
    // byTenantUserSequence is globally unique for that tenant/user.
    const auditSequence = await nextAuditSequence(
      tx,
      envelope.tenant_id,
      userId
    );

    const event: StoredGpsEvent = {
      ...ping,
      event_id: eventId,
      tenant_id: envelope.tenant_id,
      user_id: userId,
      device_id: deviceId,
      sequence_no: sequence,
      sync_status: 'PENDING_SYNC',
      command_id: commandId,
      created_at: now,
    };
    await idbRequest(tx.objectStore('driver_gps_events').add(event));

    const command: OutboxCommand = {
      outbox_id: crypto.randomUUID(), command_id: commandId,
      tenant_id: envelope.tenant_id, user_id: userId, device_id: deviceId,
      resource_type: 'driver_gps', resource_id: eventId, operation: 'UPSERT_GPS',
      schema_version: 'driver-gps-v1', app_version: APP_VERSION(), payload, payload_hash: payloadHash,
      created_at: now, sequence_no: sequence, status: 'READY', priority: 'P2',
      idempotency_key: commandId, depends_on_command_id: null, attempt_count: 0,
      last_attempt_at: null, next_attempt_at: null, server_ack_id: null,
      error_category: null, error_code: null,
    };
    await idbRequest(tx.objectStore('outbox_commands').add(command));

    const audit: LocalAuditEvent = {
      audit_id: crypto.randomUUID(), tenant_id: envelope.tenant_id, user_id: userId, device_id: deviceId,
      action: 'COMMAND_ENQUEUED', resource_type: 'driver_gps', resource_id: eventId,
      device_timestamp: now,
      sequence: auditSequence,
      app_version: APP_VERSION(),
      metadata: { command_id: commandId, payload_hash: payloadHash, dispatch_id: ping.dispatchId || null },
      sync_status: 'PENDING_SYNC',
    };
    await idbRequest(tx.objectStore('audit_events').add(audit));
  });

  requestSync('DRIVER_GPS_COMMITTED');
  return commandId;
}

export async function getQueueCount(userId?: string): Promise<number> {
  const states: OutboxCommand['status'][] = ['PENDING', 'READY', 'SYNCING', 'FAILED_RETRYABLE'];
  let count = 0;
  for (const state of states) {
    const rows = await getAllByIndex<OutboxCommand>('outbox_commands', 'byStatusPriority', IDBKeyRange.bound([state, 'P0', 0], [state, 'P4', Number.MAX_SAFE_INTEGER]));
    count += rows.filter(row => row.resource_type === 'driver_gps' && (!userId || row.user_id === userId)).length;
  }
  return count;
}

registerSyncHandler('driver_gps', 'UPSERT_GPS', async command => {
  const payload: any = command.payload;
  const response = await apiClient.put('/truck-portal/gps', {
    lat: payload.lat,
    lng: payload.lng,
    speedKmh: payload.speedKmh,
    heading: payload.heading,
    odometerKm: payload.odometerKm,
    dispatchId: payload.dispatchId,
    eventId: payload.eventId,
    observedAt: payload.capturedAt,
    accuracyM: payload.accuracyM,
  }, {
    headers: {
      'Idempotency-Key': command.idempotency_key,
      'X-Device-ID': command.device_id,
      'X-Command-Schema': command.schema_version,
      'X-App-Version': command.app_version,
      'X-Payload-Hash': command.payload_hash,
    },
  });
  const resultHash = await sha256Canonical(response.data);
  await withOfflineTransaction(['driver_gps_events'], 'readwrite', async tx => {
    const event: any = await idbRequest(tx.objectStore('driver_gps_events').get(command.resource_id));
    if (event) await idbRequest(tx.objectStore('driver_gps_events').put({
      ...event,
      sync_status: 'SYNCED',
      server_received_at:
        response.data?.received_at ||
        response.data?.updated_at ||
        new Date().toISOString(),
    }));
  });
  return {
    ack_id: response.data?.command_id || command.command_id,
    command_id: command.command_id,
    server_resource_id: response.data?.truck_id || null,
    accepted_at:
      response.data?.received_at ||
      response.data?.updated_at ||
      new Date().toISOString(),
    result_hash: resultHash,
    status: 'ACKNOWLEDGED' as const,
    resource_type: command.resource_type,
    resource_id: command.resource_id,
  };
});

export async function syncGpsQueue(_legacyApiClient?: unknown): Promise<{ synced: number; failed: number }> {
  // Compatibility shim: synchronization execution belongs exclusively to the
  // elected Offline Kernel leader. Wake that leader and report the current
  // queue state without creating a competing synchronization worker.
  const before = await getQueueCount();
  requestSync('DRIVER_GPS_LEGACY_SYNC_REQUESTED');
  const after = await getQueueCount();
  return { synced: Math.max(0, before - after), failed: after };
}

export async function clearGpsQueue(): Promise<void> {
  // Deliberate destructive clearing is not exposed anymore. Pending GPS evidence
  // must be acknowledged, cancelled through a governed workflow, or retained.
  throw new Error('Unsafe GPS queue clearing is disabled; synchronize or resolve pending records instead');
}

export function setupOfflineSync(_legacyApiClient?: unknown, onSync?: (count: number) => void): () => void {
  let lastCount = -1;
  const refresh = async () => {
    const count = await getQueueCount();
    if (lastCount >= 0 && count < lastCount && onSync) onSync(lastCount - count);
    lastCount = count;
  };
  void refresh();
  const unsubscribe = subscribeConnectivity(snapshot => {
    if (backendUsable(snapshot.state)) { requestSync('CONNECTIVITY_RESTORED'); void refresh(); }
  });
  const interval = window.setInterval(() => { requestSync('DRIVER_GPS_FOREGROUND_INTERVAL'); void refresh(); }, 30000);
  return () => { unsubscribe(); window.clearInterval(interval); };
}
